
import { useState, useEffect, useRef } from 'react';

export default function CTASection() {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener('mousemove', handleMouseMove);
      return () => section.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-800 relative overflow-hidden"
    >
      {/* Interactive Background */}
      <div 
        className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl transition-all duration-300 pointer-events-none"
        style={{
          left: mousePosition.x - 192,
          top: mousePosition.y - 192,
        }}
      ></div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-4 h-4 bg-purple-400 rounded-full animate-ping"></div>
        <div className="absolute top-40 right-20 w-3 h-3 bg-pink-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-purple-300 rounded-full animate-ping" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-5 h-5 bg-pink-300 rounded-full animate-ping" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="mb-8">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-purple-400/30 mb-6">
              <i className="ri-rocket-line text-2xl text-purple-300"></i>
              <span className="text-purple-200 font-medium">Ready to Transform Your Brand?</span>
            </div>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Ignite Your Brand's 
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Potential?</span>
          </h2>
          
          <h3 className="text-2xl md:text-3xl font-light text-purple-200 mb-8">
            Let's Define Your Unique Story.
          </h3>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            Partner with Hash Adz to build a brand identity that truly resonates and stands the test of time. 
            Your brand transformation journey starts with a single conversation.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <button className="group bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
              <span className="flex items-center gap-3">
                Request a Branding Consultation
                <i className="ri-calendar-line group-hover:rotate-12 transition-transform duration-300"></i>
              </span>
            </button>
            
            <button className="group border-2 border-purple-400 hover:bg-purple-400 text-purple-400 hover:text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
              <span className="flex items-center gap-3">
                View Our Branding Portfolio
                <i className="ri-external-link-line group-hover:rotate-45 transition-transform duration-300"></i>
              </span>
            </button>
          </div>

          {/* Stats Section */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/20">
              <div className="text-3xl font-bold text-white mb-2">150+</div>
              <div className="text-purple-200">Brands Transformed</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/20">
              <div className="text-3xl font-bold text-white mb-2">98%</div>
              <div className="text-purple-200">Client Satisfaction</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/20">
              <div className="text-3xl font-bold text-white mb-2">5+</div>
              <div className="text-purple-200">Years of Excellence</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
